import { supabase } from '../supabase';
import { useUserStore } from '../userStore';

export type PageType = 
  | 'linecombination'
  | 'free_agent'
  | 'myteam'
  | 'myoffice'
  | 'marketing_tools'
  | 'draftpage'
  | 'landingpage'
  | 'otherteam'
  | 'standing'
  | 'leaguesetting'
  | 'trade'
  | 'tutorialpage'
  | 'playerstats'
  | 'advanced_player_search'
  | 'userguide'
  | 'quick_search_player';

export interface PageCounts {
  id: number;
  free_agent_count: number | null;
  myteam_count: number | null;
  myoffice_count: number | null;
  draftpage_count: number | null;
  landingpage_count: number | null;
  otherteam_count: number | null;
  standing_count: number | null;
  leaguesetting_count: number | null;
  trade_count: number | null;
  tutorialpage_count: number | null;
  playerstats_count: number | null;
  advanced_player_search_count: number | null;
  userguide_count: number | null;
  linecombination_count: number | null;
  quick_search_player_count: number | null;
}

/**
 * Track a page view by incrementing the appropriate counter
 */
export async function trackPageView(pageType: PageType) {
  try {
    const currentUserId = useUserStore.getState().user_id;
    
    // DEBUG: Log pour diagnostiquer le problème
    console.log('🔍 DEBUG trackPageView:', { pageType, currentUserId });
    
    // Exclure le tracking pour l'utilisateur ID 3 (dev et prod)
    // user_id est un number dans le store, donc comparaison numérique
    if (currentUserId === 3 || Number(currentUserId) === 3) {
      return;
    }

    // Utiliser une fonction SQL pour incrémenter atomiquement
    console.log('📊 Tentative d\'incrémentation pour:', pageType);
    const { error } = await supabase.rpc('increment_page_count', {
      page_type: pageType
    });

    if (error) {
      console.error('❌ Erreur lors du tracking de page:', error);
    } else {
      console.log('✅ Tracking réussi pour:', pageType);
    }
  } catch (error) {
    console.warn('❌ Erreur lors du tracking de page:', error);
  }
}

/**
 * Get all page counts
 */
export async function getPageCounts(): Promise<PageCounts | null> {
  try {
    const { data, error } = await supabase
      .from('page_conter')
      .select('*')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des stats de pages:', error);
    return null;
  }
}

/**
 * Reset all page counts (admin function)
 */
export async function resetPageCounts() {
  try {
    const { error } = await supabase
      .from('page_conter')
      .update({
        free_agent_count: 0,
        myteam_count: 0,
        myoffice_count: 0,
        draftpage_count: 0,
        landingpage_count: 0,
        otherteam_count: 0,
        standing_count: 0,
        leaguesetting_count: 0,
        trade_count: 0,
        tutorialpage_count: 0,
        playerstats_count: 0,
        advanced_player_search_count: 0,
        userguide_count: 0,
        linecombination_count: 0,
        quick_search_player_count: 0
      })
      .eq('id', 1);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Erreur lors de la réinitialisation des stats:', error);
    return { success: false, error };
  }
}
